import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import { aboutContent } from '../../mocks/content';
import { Link } from 'react-router-dom';

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden pt-24">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/286e266b4567994d138da4435abe7b10.jpg"
            alt="About RE:SET"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/15 to-black/25"></div>
        </div>

        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 tracking-tight">
            About RE:SET
          </h1>
          <p className="text-lg md:text-xl text-white/95 font-light leading-relaxed">
            A wellness studio designed for those who seek more than a workout — a space to move with intention, restore with purpose, and nourish with care.
          </p>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-24 px-6 md:px-12 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-reset-text mb-6">
                Our Story
              </h2>
              <p className="text-base text-reset-text-secondary mb-6 leading-relaxed">
                RE:SET was born from a simple belief: wellness should be holistic, intentional, and accessible. We saw a gap in the industry — studios that focused on one modality, cafés that prioritized speed over nourishment, and recovery spaces that felt clinical rather than restorative.
              </p>
              <p className="text-base text-reset-text-secondary mb-6 leading-relaxed">
                We created RE:SET to bring these elements together under one roof. A place where you can move your body with precision, restore through contrast therapy, and nourish yourself with whole foods — all in a space designed to support your practice.
              </p>
              <p className="text-base text-reset-text-secondary leading-relaxed">
                Our approach is rooted in the understanding that true wellness comes from the integration of movement, recovery, and nourishment — not one in isolation.
              </p>
            </div>
            <div className="relative h-[500px] rounded-lg overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=modern%20wellness%20studio%20interior%20with%20reformer%20pilates%20equipment%20natural%20light%20wooden%20floors%20plants%20clean%20minimal%20aesthetic%20serene%20atmosphere%20professional%20photography%20neutral%20tones%20calm%20space%20high%20end%20fitness%20studio&width=800&height=1000&seq=about-story-v2&orientation=portrait"
                alt="RE:SET Studio"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Our Principles Section */}
      <section className="py-24 px-6 md:px-12 bg-reset-bg-alt">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-reset-text mb-6">
              Our Principles
            </h2>
            <p className="text-base text-reset-text-secondary max-w-2xl mx-auto">
              Everything we do is guided by these core values.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-10">
            <div className="text-center">
              <div className="w-16 h-16 bg-reset-sage-light rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-focus-3-line text-3xl text-reset-sage w-8 h-8 flex items-center justify-center"></i>
              </div>
              <h3 className="text-lg font-medium text-reset-text mb-3">
                Intentionality
              </h3>
              <p className="text-sm text-reset-text-secondary leading-relaxed">
                Every detail — from the lighting to the temperature, the materials to the music — is designed to support your practice.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-reset-sage-light rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-plant-line text-3xl text-reset-sage w-8 h-8 flex items-center justify-center"></i>
              </div>
              <h3 className="text-lg font-medium text-reset-text mb-3">
                Holistic Approach
              </h3>
              <p className="text-sm text-reset-text-secondary leading-relaxed">
                We believe wellness is the integration of movement, recovery, and nourishment — not one modality in isolation.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-reset-sage-light rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-community-line text-3xl text-reset-sage w-8 h-8 flex items-center justify-center"></i>
              </div>
              <h3 className="text-lg font-medium text-reset-text mb-3">
                Community
              </h3>
              <p className="text-sm text-reset-text-secondary leading-relaxed">
                We're building a space where people can connect, support each other, and grow together in their wellness journey.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-reset-sage-light rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-shield-check-line text-3xl text-reset-sage w-8 h-8 flex items-center justify-center"></i>
              </div>
              <h3 className="text-lg font-medium text-reset-text mb-3">
                Quality
              </h3>
              <p className="text-sm text-reset-text-secondary leading-relaxed">
                From our equipment to our ingredients, we invest in the best so you can experience the difference.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-reset-sage-light rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-compass-3-line text-3xl text-reset-sage w-8 h-8 flex items-center justify-center"></i>
              </div>
              <h3 className="text-lg font-medium text-reset-text mb-3">
                Guidance
              </h3>
              <p className="text-sm text-reset-text-secondary leading-relaxed">
                Our instructors bring expertise, presence, and a deep commitment to helping you move with precision and purpose.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-reset-sage-light rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-time-line text-3xl text-reset-sage w-8 h-8 flex items-center justify-center"></i>
              </div>
              <h3 className="text-lg font-medium text-reset-text mb-3">
                Sustainability
              </h3>
              <p className="text-sm text-reset-text-secondary leading-relaxed">
                We're committed to practices that honor both your body and the planet — from our sourcing to our operations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-24 px-6 md:px-12 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-reset-text mb-6">
              Led by Experts
            </h2>
            <p className="text-base text-reset-text-secondary max-w-2xl mx-auto">
              Our team brings decades of combined experience in movement, wellness, and hospitality.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-10">
            <div className="text-center">
              <div className="relative h-80 rounded-lg overflow-hidden mb-6">
                <img
                  src="https://readdy.ai/api/search-image?query=professional%20female%20pilates%20instructor%20in%20modern%20wellness%20studio%20wearing%20neutral%20athletic%20wear%20confident%20pose%20natural%20light%20clean%20minimal%20background%20professional%20portrait%20photography%20serene%20expression%20high%20quality&width=600&height=800&seq=about-instructor-1-v2&orientation=portrait"
                  alt="Sarah Mitchell"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <h3 className="text-xl font-semibold text-reset-text mb-2">
                Sarah Mitchell
              </h3>
              <p className="text-sm text-reset-sage font-medium mb-3">
                Founder & Lead Instructor
              </p>
              <p className="text-sm text-reset-text-secondary leading-relaxed">
                Certified Pilates instructor with 15+ years of experience. Sarah trained at the Kane School and has worked with elite athletes and wellness seekers alike.
              </p>
            </div>

            <div className="text-center">
              <div className="relative h-80 rounded-lg overflow-hidden mb-6">
                <img
                  src="https://readdy.ai/api/search-image?query=professional%20male%20wellness%20coach%20in%20modern%20studio%20wearing%20neutral%20athletic%20wear%20confident%20pose%20natural%20light%20clean%20minimal%20background%20professional%20portrait%20photography%20calm%20expression%20high%20quality&width=600&height=800&seq=about-instructor-2-v2&orientation=portrait"
                  alt="James Chen"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <h3 className="text-xl font-semibold text-reset-text mb-2">
                James Chen
              </h3>
              <p className="text-sm text-reset-sage font-medium mb-3">
                Wellness Director
              </p>
              <p className="text-sm text-reset-text-secondary leading-relaxed">
                Certified in contrast therapy and recovery protocols. James brings a holistic approach to wellness, integrating movement and restoration.
              </p>
            </div>

            <div className="text-center">
              <div className="relative h-80 rounded-lg overflow-hidden mb-6">
                <img
                  src="https://readdy.ai/api/search-image?query=professional%20female%20nutritionist%20in%20modern%20wellness%20cafe%20wearing%20neutral%20casual%20wear%20confident%20pose%20natural%20light%20clean%20minimal%20background%20professional%20portrait%20photography%20warm%20expression%20high%20quality&width=600&height=800&seq=about-instructor-3-v2&orientation=portrait"
                  alt="Emma Rodriguez"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <h3 className="text-xl font-semibold text-reset-text mb-2">
                Emma Rodriguez
              </h3>
              <p className="text-sm text-reset-sage font-medium mb-3">
                Café Director
              </p>
              <p className="text-sm text-reset-text-secondary leading-relaxed">
                Certified nutritionist and chef. Emma curates our menu with a focus on whole foods, seasonal ingredients, and nourishment that supports your practice.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 md:px-12 bg-reset-blue-light">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-reset-text mb-6">
            Join Our Community
          </h2>
          <p className="text-base text-reset-text-secondary mb-10">
            Experience the RE:SET difference with a complimentary intro session.
          </p>
          <Link
            to="/contact"
            className="inline-block bg-reset-sage text-white hover:bg-reset-sage-hover px-10 py-4 rounded-full text-sm font-semibold tracking-widest transition-all duration-300 whitespace-nowrap cursor-pointer uppercase shadow-lg hover:shadow-xl"
          >
            BOOK YOUR INTRO
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}